import type { StateStorage } from 'zustand/middleware';

/**
 * Per-user namespace for every persisted localStorage key. Apple's reference
 * is `RenderDraftManager.swift:36-51`. Without this prefix, drafts and
 * preferences of user A on a shared device are visible to user B after
 * logout/login.
 *
 * The leading `${userId}:` segment also makes wipe-on-logout trivial — see
 * {@link wipeUserScopedKeys}.
 *
 * This module is a leaf utility: it must NOT import from `@/features/*` or
 * the `@/shared/lib` barrel. The auth store is wired in at boot via
 * {@link initUserScopedStorage} — see `app/store-initializer.tsx`. Importing
 * `auth.store` here re-introduces an `auth.store ↔ shared/lib barrel ↔
 * @/features/render ↔ album-settings.store ↔ user-scoped-storage` cycle that
 * crashes module evaluation with TDZ.
 */
export const ANONYMOUS_SCOPE = 'anonymous';

type ScopeReader = () => string | undefined;
type ScopeSubscriber = (cb: (next: string, prev: string) => void) => void;

let readScope: ScopeReader = () => undefined;

/**
 * Current scope as a localStorage key prefix. Returns `'anonymous'` until
 * {@link initUserScopedStorage} is called or while no user is signed in.
 */
export function getCurrentUserScope(): string {
  return readScope() ?? ANONYMOUS_SCOPE;
}

/**
 * Build a userId-scoped localStorage key. Pass `userId` explicitly when wiping
 * keys for a previous identity (the auth store has already moved on).
 */
export function userScopedKey(name: string, userId?: string): string {
  return `${userId ?? getCurrentUserScope()}:${name}`;
}

/**
 * Zustand `StateStorage` that prefixes the persist `name` with the current
 * user id at read/write time. Drop into `createJSONStorage(() =>
 * userScopedStorage())`.
 */
export function userScopedStorage(): StateStorage {
  return {
    getItem: (name) => {
      try {
        return localStorage.getItem(userScopedKey(name));
      } catch {
        return null;
      }
    },
    setItem: (name, value) => {
      try {
        localStorage.setItem(userScopedKey(name), value);
      } catch {
        /* full / disabled */
      }
    },
    removeItem: (name) => {
      try {
        localStorage.removeItem(userScopedKey(name));
      } catch {
        /* ignore */
      }
    },
  };
}

/**
 * Remove every localStorage key that belongs to a user scope. Called from
 * `cacheInvalidation.afterLogout(userId)` so the next account on this device
 * starts clean.
 */
export function wipeUserScopedKeys(userId: string | null | undefined): void {
  if (!userId) return;
  try {
    const prefix = `${userId}:`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

// --- Re-hydration on identity switch -----------------------------------

interface RehydratableStore {
  persist: { rehydrate: () => Promise<void> | void };
}

const registeredStores: RehydratableStore[] = [];

/**
 * Register a Zustand persist store so it gets re-hydrated when the
 * authenticated user changes (login → logout → login as someone else, or
 * a silent identity switch detected by `refreshUser`). Without this, the
 * store would keep the previous user's hydrated state in memory after the
 * scope flipped.
 */
export function registerUserScopedStore(store: RehydratableStore): void {
  registeredStores.push(store);
}

let initialized = false;

/**
 * Wire the user-scoped store registry to the auth store. Call once at app
 * boot from `store-initializer.tsx`, passing the auth-store accessors:
 *
 * ```ts
 * initUserScopedStorage({
 *   readScope: () => queryClient.getQueryData<User>(cacheKeys.user)?.id,
 *   subscribeScope: (cb) => queryClient.getQueryCache().subscribe(...),
 * });
 * ```
 *
 * Injection (rather than importing the auth store / Query client here) keeps
 * this module a pure leaf — no feature dependencies — and breaks the
 * `auth.store` cycle that otherwise traps `registerUserScopedStore` in TDZ
 * during module eval.
 */
export function initUserScopedStorage(opts: {
  readScope: ScopeReader;
  subscribeScope: ScopeSubscriber;
}): void {
  if (initialized) return;
  initialized = true;
  readScope = opts.readScope;
  opts.subscribeScope((newScope, prevScope) => {
    if (newScope === prevScope) return;
    registeredStores.forEach((s) => {
      void s.persist.rehydrate();
    });
  });
}
